<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, nextTick, ref, type ComputedRef, type Ref } from "vue";

interface Props {
  readonly modelValue: string;
  readonly twelveHour: boolean;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
}

const props: Props = withDefaults(defineProps<Props>(), { disabled: false });
const emit: (event: "update:modelValue", value: string) => void = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();

const open: Ref<boolean> = ref(false);
const hourInput: Ref<HTMLInputElement | null> = ref(null);
const numericHour: ComputedRef<number> = computed((): number => Number(props.modelValue.slice(0, 2)));
const hour: ComputedRef<number> = computed((): number => props.twelveHour ? numericHour.value % 12 || 12 : numericHour.value);
const minute: ComputedRef<number> = computed((): number => Number(props.modelValue.slice(3, 5)));
const meridiem: ComputedRef<"AM" | "PM"> = computed((): "AM" | "PM" => numericHour.value < 12 ? "AM" : "PM");
const displayValue: ComputedRef<string> = computed((): string => props.twelveHour
  ? `${hour.value}:${String(minute.value).padStart(2, "0")} ${meridiem.value}`
  : `${String(hour.value).padStart(2, "0")}:${String(minute.value).padStart(2, "0")}`,
);

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, Math.trunc(value)));
}
function update(hourValue: number, minuteValue: number, meridiemValue: "AM" | "PM"): void {
  const normalizedMinute: number = clamp(minuteValue, 0, 59);
  const normalizedHour: number = props.twelveHour
    ? clamp(hourValue, 1, 12) % 12 + (meridiemValue === "PM" ? 12 : 0)
    : clamp(hourValue, 0, 23);
  emit("update:modelValue", `${String(normalizedHour).padStart(2, "0")}:${String(normalizedMinute).padStart(2, "0")}`);
}
function readNumber(event: Event, fallback: number): number {
  const value: number = Number((event.target as HTMLInputElement).value);
  return Number.isFinite(value) ? value : fallback;
}
async function toggle(): Promise<void> {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    hourInput.value?.focus();
    hourInput.value?.select();
  }
}
function close(): void { open.value = false; }
</script>

<template>
  <div class="time-picker">
    <button
      class="time-picker__trigger"
      type="button"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      aria-haspopup="dialog"
      :disabled="disabled"
      @click="toggle"
      @keydown.escape="close"
    >
      <span>{{ displayValue }}</span><span aria-hidden="true">⌄</span>
    </button>
    <div v-if="open" class="time-picker__popover" role="dialog" :aria-label="`${ariaLabel} picker`" @keydown.escape="close">
      <input ref="hourInput" class="time-picker__number" :value="hour" type="number" :min="twelveHour ? 1 : 0" :max="twelveHour ? 12 : 23" inputmode="numeric" @change="update(readNumber($event, hour), minute, meridiem)" />
      <span aria-hidden="true">:</span>
      <input class="time-picker__number" :value="String(minute).padStart(2, '0')" type="number" min="0" max="59" inputmode="numeric" @change="update(hour, readNumber($event, minute), meridiem)" />
      <select v-if="twelveHour" :value="meridiem" aria-label="AM or PM" @change="update(hour, minute, ($event.target as HTMLSelectElement).value as 'AM' | 'PM')"><option>AM</option><option>PM</option></select>
      <button class="button button--quiet button--compact" type="button" @click="close">Done</button>
    </div>
  </div>
</template>

<style scoped>
.time-picker { position: relative; }
.time-picker__trigger { width: 100%; min-height: 42px; padding: 0.68rem 0.8rem; border: 1px solid var(--border); border-radius: 8px; color: var(--text); background: rgba(7, 17, 31, 0.65); display: flex; justify-content: space-between; align-items: center; font: inherit; cursor: pointer; }
.time-picker__trigger:hover:not(:disabled), .time-picker__trigger:focus-visible:not(:disabled) { border-color: rgba(55, 215, 160, 0.6); outline: none; }
.time-picker__trigger:disabled { cursor: not-allowed; color: var(--muted); background: rgba(7, 17, 31, 0.36); opacity: 0.62; }
.time-picker__popover { position: absolute; z-index: 10; top: calc(100% + 0.35rem); left: 0; display: flex; align-items: center; gap: 0.35rem; padding: 0.5rem; border: 1px solid var(--border); border-radius: 8px; background: #0b1a2c; box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35); }
.time-picker__number, .time-picker__popover select { min-width: 4.2rem; min-height: 38px; padding: 0.4rem; border: 1px solid var(--border); border-radius: 6px; color: var(--text); background: rgba(7, 17, 31, 0.8); font: inherit; }
</style>
